/**
 * /admin/audit — admin-only audit log viewer over /api/audit.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { type ApiError, api } from '../lib/api';
import { formatDate } from '../lib/format';
import { Badge, Banner } from '../components/brand';
import { Combobox } from '../components/Combobox';
import { DataTable, type DataTableColumn } from '../components/DataTable';

interface AuditRow {
  id: number;
  userId: string | null;
  action: string;
  accountId: string | null;
  bucket: string | null;
  objectKey: string | null;
  requestIp: string | null;
  userAgent: string | null;
  status: 'ok' | 'denied' | 'error';
  detail: unknown;
  createdAt: number;
}

interface AuditPage {
  items: AuditRow[];
  cursor: string | null;
}

type Status = '' | 'ok' | 'denied' | 'error';

function toQuery(status: Status, cursor: string | null): string {
  const p = new URLSearchParams();
  if (status) p.set('status', status);
  p.set('limit', '100');
  if (cursor) p.set('cursor', cursor);
  return `?${p.toString()}`;
}

export function AdminAudit() {
  const [status, setStatus] = useState<Status>('');
  const [pages, setPages] = useState<AuditPage[]>([]);

  const initial = useQuery({
    queryKey: ['audit', status],
    queryFn: () => api.get<AuditPage>(`/api/audit${toQuery(status, null)}`),
  });

  useEffect(() => {
    if (initial.data) setPages([initial.data]);
  }, [initial.data]);

  async function loadMore() {
    const last = pages[pages.length - 1];
    if (!last?.cursor) return;
    const next = await api.get<AuditPage>(`/api/audit${toQuery(status, last.cursor)}`);
    setPages((p) => [...p, next]);
  }

  const items = pages.flatMap((p) => p.items);
  const lastCursor = pages.length > 0 ? (pages[pages.length - 1]?.cursor ?? null) : null;

  const columns: DataTableColumn<AuditRow>[] = [
    {
      id: 'time',
      header: 'Time',
      width: 170,
      sortable: true,
      accessor: (r) => r.createdAt,
      cell: (r) => (
        <span className="mono tnum" style={{ fontSize: 12, color: 'var(--bm-text-muted)' }}>
          {formatDate(r.createdAt)}
        </span>
      ),
    },
    {
      id: 'user',
      header: 'User',
      width: 160,
      defaultHidden: true,
      accessor: (r) => r.userId ?? '',
      cell: (r) => (
        <span className="mono" style={{ fontSize: 12 }} title={r.userId ?? ''}>
          {r.userId ?? '—'}
        </span>
      ),
    },
    {
      id: 'action',
      header: 'Action',
      width: 200,
      sortable: true,
      accessor: (r) => r.action,
      cell: (r) => (
        <span className="mono" style={{ fontSize: 12 }}>
          {r.action}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      width: 80,
      sortable: true,
      accessor: (r) => r.status,
      cell: (r) => <Badge variant={statusVariant(r.status)}>{r.status}</Badge>,
    },
    {
      id: 'target',
      header: 'Target',
      accessor: (r) => renderTarget(r),
      cell: (r) => (
        <span
          className="mono"
          style={{
            fontSize: 12,
            color: 'var(--bm-text-soft)',
            display: 'inline-block',
            maxWidth: 320,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            verticalAlign: 'middle',
          }}
          title={renderTarget(r)}
        >
          {renderTarget(r) || '—'}
        </span>
      ),
    },
    {
      id: 'detail',
      header: 'Detail',
      defaultHidden: true,
      accessor: (r) => (r.detail ? JSON.stringify(r.detail) : ''),
      cell: (r) => (
        <span
          className="mono"
          style={{
            fontSize: 11,
            color: 'var(--bm-text-muted)',
            display: 'inline-block',
            maxWidth: 280,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            verticalAlign: 'middle',
          }}
          title={r.detail ? JSON.stringify(r.detail) : ''}
        >
          {r.detail ? JSON.stringify(r.detail) : ''}
        </span>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 className="h-page">Audit log</h1>
        <p className="muted" style={{ marginTop: 6 }}>
          Every privileged or stateful action. Tail freshly from the database — pageable,
          filterable.
        </p>
      </div>

      {initial.error && (
        <div style={{ marginBottom: 16 }}>
          <Banner variant="danger" code={(initial.error as ApiError).code ?? 'ERROR'}>
            {(initial.error as Error).message}
          </Banner>
        </div>
      )}

      <DataTable
        data={items}
        columns={columns}
        rowKey={(r) => String(r.id)}
        loading={initial.isLoading}
        emptyLabel="No rows match the current filters."
        searchText={(r) =>
          `${r.userId ?? ''} ${r.action} ${r.accountId ?? ''} ${r.bucket ?? ''} ${r.objectKey ?? ''} ${r.detail ? JSON.stringify(r.detail) : ''}`
        }
        searchPlaceholder="Search loaded rows…"
        filters={
          <div style={{ width: 140 }}>
            <Combobox
              value={status}
              options={[
                { value: '', label: 'any status' },
                { value: 'ok', label: 'ok' },
                { value: 'denied', label: 'denied' },
                { value: 'error', label: 'error' },
              ]}
              onChange={(v) => {
                setStatus(v as Status);
                setPages([]);
              }}
              placeholder="any status"
            />
          </div>
        }
      />

      {lastCursor && (
        <div className="bm-row" style={{ justifyContent: 'flex-end', marginTop: 12 }}>
          <button type="button" className="bm-btn bm-btn-ghost bm-btn-sm" onClick={loadMore}>
            Load more
          </button>
        </div>
      )}
    </div>
  );
}

function statusVariant(s: AuditRow['status']): 'success' | 'warn' | 'danger' {
  if (s === 'ok') return 'success';
  if (s === 'denied') return 'warn';
  return 'danger';
}

function renderTarget(row: AuditRow): string {
  const parts: string[] = [];
  if (row.accountId) parts.push(row.accountId);
  if (row.bucket) parts.push(row.bucket);
  if (row.objectKey) parts.push(row.objectKey);
  return parts.join(' / ');
}
